import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import s from './Leadership.module.css';
import { PATHS } from '../../../paths';

import { GET_ME } from '../../../queries/getMe';
import { GET_SUPPORTED_COMMUNITIES } from '../../../queries/getSupportedCommunities';
import { GET_PRAYER_MEETINGS } from '../../../queries/getPrayerMeetings';
import { GET_JOYRIDE } from '../../../queries/getJoyride';
import { GET_EXPOSURE_TRIPS } from '../../../queries/getExposureTrips';

import { CREATE_PRAYER_MEETING } from '../../../mutations/CREATE_PRAYER_MEETING';
import { UPDATE_PRAYER_MEETING } from '../../../mutations/UPDATE_PRAYER_MEETING';
import { ARCHIVE_PRAYER_MEETING } from '../../../mutations/ARCHIVE_PRAYER_MEETING';
import { SET_JOYRIDE } from '../../../mutations/SET_JOYRIDE';
import { CREATE_EXPOSURE_TRIP } from '../../../mutations/CREATE_EXPOSURE_TRIP';
import { CREATE_LEADERSHIP_POST } from '../../../mutations/CREATE_LEADERSHIP_POST';
import { CREATE_IMAGE_UPLOAD } from '../../../mutations/CREATE_IMAGE_UPLOAD';
import { UPDATE_EXPOSURE_TRIP } from '../../../mutations/UPDATE_EXPOSURE_TRIP';
import { ARCHIVE_EXPOSURE_TRIP } from '../../../mutations/ARCHIVE_EXPOSURE_TRIP';

type Community = { supportedOrganisationId: number; scope: string; name: string; scopeLabel?: string | null };
type PM = { id: number; name: string; location: string; time: string; frequency: string };
type Interest = { id: number; expressedAt: string; person: { id: number; name: string } | null };
type Trip = { id: number; name: string; date: string; location: string; durationDays: number; description: string; interestedCount?: number; interested?: Interest[] };

const EMPTY_PM: Omit<PM, 'id'> = { name: '', location: '', time: '', frequency: '' };
const EMPTY_TRIP: Omit<Trip, 'id'> = { name: '', date: '', location: '', durationDays: 0, description: '' };

function formatInterestDate(iso: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('default', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function formatTripDate(iso: string) {
  if (!iso) return { day: '—', month: '' };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { day: '—', month: '' };
  return {
    day: d.getUTCDate().toString(),
    month: d.toLocaleString('default', { month: 'short', timeZone: 'UTC' }).toUpperCase(),
  };
}

// ── Prayer Meetings ─────────────────────────────────────────────────────────

function PrayerMeetingsSection({ orgId }: { orgId: number }) {
  const { data, refetch } = useQuery(GET_PRAYER_MEETINGS, { variables: { supportedOrganisationId: orgId } });
  const [edits, setEdits] = useState<Record<number, PM>>({});
  const [newPM, setNewPM] = useState({ ...EMPTY_PM });
  const [adding, setAdding] = useState(false);

  const [create] = useMutation(CREATE_PRAYER_MEETING, { onCompleted: () => { setNewPM({ ...EMPTY_PM }); setAdding(false); refetch(); } });
  const [update] = useMutation(UPDATE_PRAYER_MEETING, { onCompleted: () => refetch() });
  const [archive] = useMutation(ARCHIVE_PRAYER_MEETING, { onCompleted: () => refetch() });

  const meetings: PM[] = data?.prayerMeetings ?? [];

  const patchEdit = (id: number, base: PM, patch: Partial<PM>) =>
    setEdits(prev => ({ ...prev, [id]: { ...base, ...prev[id], ...patch } }));

  const saveEdit = (id: number, row: PM) => {
    update({ variables: { ...row, supportedOrganisationId: orgId } });
    setEdits(prev => { const next = { ...prev }; delete next[id]; return next; });
  };

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Prayer Meetings</h2>
        <span className={s.sectionTag}>Community</span>
      </div>
      <div className={s.pmGrid}>
        {meetings.map(m => {
          const row = edits[m.id] ?? m;
          const isEditing = !!edits[m.id];
          return (
            <div key={m.id} className={`${s.pmCard} ${isEditing ? s.editing : ''}`}>
              {isEditing ? (
                <>
                  <input className={s.input} value={row.name} placeholder="Meeting name"
                    onChange={e => patchEdit(m.id, m, { name: e.target.value })} />
                  <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                    <input className={s.input} value={row.location ?? ''} placeholder="Location"
                      onChange={e => patchEdit(m.id, m, { location: e.target.value })} />
                    <input className={s.input} value={row.time ?? ''} placeholder="Time  e.g. Sundays 8am"
                      onChange={e => patchEdit(m.id, m, { time: e.target.value })} />
                    <input className={s.input} value={row.frequency ?? ''} placeholder="Frequency  e.g. Weekly"
                      onChange={e => patchEdit(m.id, m, { frequency: e.target.value })} />
                  </div>
                  <div className={s.pmActions}>
                    <button className={`${s.btn} ${s.btnPrimary} ${s.btnSm}`} onClick={() => saveEdit(m.id, row)}>Save</button>
                    <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                      onClick={() => setEdits(prev => { const n = { ...prev }; delete n[m.id]; return n; })}>Cancel</button>
                    <button className={`${s.btn} ${s.btnDanger} ${s.btnSm}`}
                      onClick={() => archive({ variables: { id: m.id, supportedOrganisationId: orgId } })}>Delete</button>
                  </div>
                </>
              ) : (
                <>
                  <div className={s.pmName}>{m.name}</div>
                  <div className={s.pmMeta}>
                    {m.location && (
                      <div className={s.pmMetaRow}>
                        <span className={s.pmMetaIcon}>📍</span>
                        <span>{m.location}</span>
                      </div>
                    )}
                    {m.time && (
                      <div className={s.pmMetaRow}>
                        <span className={s.pmMetaIcon}>🕐</span>
                        <span>{m.time}</span>
                      </div>
                    )}
                    {m.frequency && <span className={s.freqBadge}>{m.frequency}</span>}
                  </div>
                  <div className={s.pmActions}>
                    <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                      onClick={() => patchEdit(m.id, m, {})}>Edit</button>
                  </div>
                </>
              )}
            </div>
          );
        })}

        {adding ? (
          <div className={`${s.pmCard} ${s.editing}`}>
            <input className={s.input} value={newPM.name} placeholder="Meeting name"
              onChange={e => setNewPM(p => ({ ...p, name: e.target.value }))} autoFocus />
            <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
              <input className={s.input} value={newPM.location} placeholder="Location"
                onChange={e => setNewPM(p => ({ ...p, location: e.target.value }))} />
              <input className={s.input} value={newPM.time} placeholder="Time  e.g. Sundays 8am"
                onChange={e => setNewPM(p => ({ ...p, time: e.target.value }))} />
              <input className={s.input} value={newPM.frequency} placeholder="Frequency  e.g. Weekly"
                onChange={e => setNewPM(p => ({ ...p, frequency: e.target.value }))} />
            </div>
            <div className={s.pmActions}>
              <button className={`${s.btn} ${s.btnPrimary} ${s.btnSm}`}
                onClick={() => create({ variables: { supportedOrganisationId: orgId, ...newPM } })}>Add</button>
              <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`} onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <button className={s.addCard} onClick={() => setAdding(true)}>
            <span className={s.addIcon}>+</span>
            <span>Add meeting</span>
          </button>
        )}

        {meetings.length === 0 && !adding && (
          <p className={s.empty}>No prayer meetings yet — add the first one.</p>
        )}
      </div>
    </div>
  );
}

// ── Joyride ─────────────────────────────────────────────────────────────────

function JoyrideSection() {
  const { data, refetch } = useQuery(GET_JOYRIDE);
  const joyride = data?.joyride;

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [challenge, setChallenge] = useState('');
  const [target, setTarget] = useState('');
  const [saved, setSaved] = useState(false);
  const [saveKey, setSaveKey] = useState(0);

  const [setJoyrideMutation] = useMutation(SET_JOYRIDE, {
    onCompleted: () => {
      refetch();
      setSaved(true);
      setSaveKey(k => k + 1);
      setTimeout(() => setSaved(false), 2100);
    },
  });

  const handleSave = () => {
    setJoyrideMutation({
      variables: {
        name: name || joyride?.name || undefined,
        startDate: startDate ? startDate.toISOString() : (joyride?.startDate ?? undefined),
        endDate: endDate ? endDate.toISOString() : (joyride?.endDate ?? undefined),
        challenge: challenge || joyride?.challenge || undefined,
        target: target ? parseInt(target, 10) : (joyride?.target ?? undefined),
      },
    });
  };

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Joyride</h2>
        <span className={s.sectionTag}>Org-wide</span>
      </div>
      <div className={s.joyrideBanner}>
        <div className={s.joyrideFields}>
          <div className={s.field}>
            <label className={s.fieldLabel}>Campaign name</label>
            <input className={s.input} value={name} placeholder={joyride?.name ?? 'e.g. Joyride 2026'}
              onChange={e => setName(e.target.value)} />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Start date</label>
            <DatePicker
              selected={startDate}
              onChange={d => setStartDate(d)}
              placeholderText={joyride?.startDate ? joyride.startDate.slice(0, 10) : 'Start'}
              dateFormat="d MMM yyyy"
              customInput={<input className={s.input} />}
              portalId="joyride-datepicker-portal"
            />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>End date</label>
            <DatePicker
              selected={endDate}
              onChange={d => setEndDate(d)}
              placeholderText={joyride?.endDate ? joyride.endDate.slice(0, 10) : 'End'}
              dateFormat="d MMM yyyy"
              customInput={<input className={s.input} />}
              portalId="joyride-datepicker-portal"
            />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Challenge</label>
            <input className={s.input} value={challenge} placeholder={joyride?.challenge ?? 'e.g. 300 km'}
              onChange={e => setChallenge(e.target.value)} />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Goal ($)</label>
            <input className={s.input} type="number" value={target}
              placeholder={joyride?.target != null ? joyride.target.toLocaleString() : '50000'}
              onChange={e => setTarget(e.target.value)} />
          </div>
          <div className={s.field} style={{ justifyContent: 'flex-end' }}>
            <label className={s.fieldLabel}>&nbsp;</label>
            <button className={`${s.btn} ${s.btnPrimary}`} onClick={handleSave}>Save campaign</button>
          </div>
        </div>
        {saved && (
          <div key={saveKey} className={s.joyrideStatus}>
            <span className={s.savedPill}>✓ Saved</span>
          </div>
        )}
        {!saved && joyride?.name && (
          <div className={s.joyrideStatus}>
            <span className={s.joyrideStatusDot} />
            <span>Current: {joyride.name}
              {joyride.startDate && ` · ${joyride.startDate.slice(0, 10)}`}
              {joyride.endDate && ` – ${joyride.endDate.slice(0, 10)}`}
              {joyride.target != null && ` · $${joyride.target.toLocaleString()} goal`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Community Visits ─────────────────────────────────────────────────────────

function VisitsSection({ orgId }: { orgId: number }) {
  const { data, refetch } = useQuery(GET_EXPOSURE_TRIPS, { variables: { supportedOrganisationId: orgId } });
  const [edits, setEdits] = useState<Record<number, Trip>>({});
  const [newTrip, setNewTrip] = useState({ ...EMPTY_TRIP });
  const [adding, setAdding] = useState(false);

  const [create] = useMutation(CREATE_EXPOSURE_TRIP, { onCompleted: () => { setNewTrip({ ...EMPTY_TRIP }); setAdding(false); refetch(); } });
  const [update] = useMutation(UPDATE_EXPOSURE_TRIP, { onCompleted: () => refetch() });
  const [archive] = useMutation(ARCHIVE_EXPOSURE_TRIP, { onCompleted: () => refetch() });

  const trips: Trip[] = data?.exposureTrips ?? [];

  const patchEdit = (id: number, base: Trip, patch: Partial<Trip>) =>
    setEdits(prev => ({ ...prev, [id]: { ...base, ...prev[id], ...patch } }));

  const saveEdit = (id: number, row: Trip) => {
    update({ variables: { ...row, supportedOrganisationId: orgId } });
    setEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Community Visits</h2>
        <span className={`${s.sectionTag} ${s.sectionTagJade}`}>Community</span>
      </div>
      <div className={s.visitList}>
        {trips.map(t => {
          const row = edits[t.id] ?? t;
          const isEditing = !!edits[t.id];
          const { day, month } = formatTripDate(t.date);

          if (isEditing) {
            return (
              <div key={t.id} className={`${s.visitCard} ${s.editing}`}>
                <div />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                  <div className={s.field}>
                    <label className={s.fieldLabel}>Name</label>
                    <input className={s.input} value={row.name ?? ''} placeholder="Trip name"
                      onChange={e => patchEdit(t.id, t, { name: e.target.value })} />
                  </div>
                  <div className={s.field}>
                    <label className={s.fieldLabel}>Date</label>
                    <DatePicker
                      selected={row.date ? new Date(row.date) : null}
                      onChange={d => patchEdit(t.id, t, { date: d ? d.toISOString() : '' })}
                      dateFormat="d MMM yyyy"
                      customInput={<input className={s.input} />}
                      placeholderText="Date"
                    />
                  </div>
                  <div className={s.field}>
                    <label className={s.fieldLabel}>Location</label>
                    <input className={s.input} value={row.location ?? ''} placeholder="Location"
                      onChange={e => patchEdit(t.id, t, { location: e.target.value })} />
                  </div>
                  <div className={s.field}>
                    <label className={s.fieldLabel}>Duration (days)</label>
                    <input className={s.input} type="number" value={row.durationDays || ''}
                      placeholder="10"
                      onChange={e => patchEdit(t.id, t, { durationDays: parseInt(e.target.value) || 0 })} />
                  </div>
                  <div className={s.field} style={{ gridColumn: '1 / -1' }}>
                    <label className={s.fieldLabel}>Description</label>
                    <input className={s.input} value={row.description ?? ''} placeholder="Details about the trip"
                      onChange={e => patchEdit(t.id, t, { description: e.target.value })} />
                  </div>
                </div>
                <div className={s.visitActions}>
                  <button className={`${s.btn} ${s.btnPrimary} ${s.btnSm}`} onClick={() => saveEdit(t.id, row)}>Save</button>
                  <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                    onClick={() => setEdits(prev => { const n = { ...prev }; delete n[t.id]; return n; })}>Cancel</button>
                  <button className={`${s.btn} ${s.btnDanger} ${s.btnSm}`}
                    onClick={() => archive({ variables: { id: t.id, supportedOrganisationId: orgId } })}>Archive</button>
                </div>
              </div>
            );
          }

          return (
            <div key={t.id} className={s.visitCard}>
              <div className={s.visitDateBlock}>
                <div className={s.visitDay}>{day}</div>
                <div className={s.visitMonth}>{month}</div>
              </div>
              <div className={s.visitInfo}>
                <div className={s.visitName}>{t.name || 'Untitled visit'}</div>
                <div className={s.visitMeta}>
                  {t.location && <span>{t.location}</span>}
                  {t.durationDays ? <>{t.location ? ' · ' : ''}<span className={s.durationBadge}>{t.durationDays} days</span></> : null}
                  {t.description && <div style={{ marginTop: '0.25rem', opacity: 0.7 }}>{t.description}</div>}
                </div>
              </div>
              <div className={s.visitActions}>
                <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                  onClick={() => patchEdit(t.id, t, {})}>Edit</button>
              </div>

              <div className={s.interestBlock}>
                <div className={s.interestHead}>
                  <span className={s.interestCount}>{t.interestedCount ?? 0}</span>
                  <span>{(t.interestedCount ?? 0) === 1 ? 'person interested' : 'people interested'}</span>
                </div>
                {t.interested && t.interested.length > 0 && (
                  <ul className={s.interestList}>
                    {t.interested.map(i => (
                      <li key={i.id} className={s.interestItem}>
                        <span className={s.interestName}>{i.person?.name ?? 'Unknown'}</span>
                        <span className={s.interestDate}>{formatInterestDate(i.expressedAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}

        {adding ? (
          <div className={`${s.visitCard} ${s.editing}`} style={{ gridTemplateColumns: '1fr' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              <div className={s.field}>
                <label className={s.fieldLabel}>Name</label>
                <input className={s.input} value={newTrip.name} placeholder="Trip name" autoFocus
                  onChange={e => setNewTrip(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Date</label>
                <DatePicker
                  selected={newTrip.date ? new Date(newTrip.date) : null}
                  onChange={d => setNewTrip(p => ({ ...p, date: d ? d.toISOString() : '' }))}
                  dateFormat="d MMM yyyy"
                  customInput={<input className={s.input} />}
                  placeholderText="Date"
                />
              </div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Location</label>
                <input className={s.input} value={newTrip.location} placeholder="e.g. Kolkata, West Bengal"
                  onChange={e => setNewTrip(p => ({ ...p, location: e.target.value }))} />
              </div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Duration (days)</label>
                <input className={s.input} type="number" value={newTrip.durationDays || ''}
                  placeholder="10"
                  onChange={e => setNewTrip(p => ({ ...p, durationDays: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className={s.field} style={{ gridColumn: '1 / -1' }}>
                <label className={s.fieldLabel}>Description</label>
                <input className={s.input} value={newTrip.description} placeholder="Details about the trip"
                  onChange={e => setNewTrip(p => ({ ...p, description: e.target.value }))} />
              </div>
            </div>
            <div className={s.pmActions} style={{ marginTop: '0.85rem' }}>
              <button className={`${s.btn} ${s.btnPrimary} ${s.btnSm}`}
                onClick={() => create({ variables: { supportedOrganisationId: orgId, ...newTrip } })}>Add visit</button>
              <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`} onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <button className={s.addCard} style={{ minHeight: 64, flexDirection: 'row', justifyContent: 'flex-start', padding: '1rem 1.25rem' }}
            onClick={() => setAdding(true)}>
            <span className={s.addIcon} style={{ fontSize: '1.1rem' }}>+</span>
            <span>Add community visit</span>
          </button>
        )}

        {trips.length === 0 && !adding && (
          <p className={s.empty}>No visits scheduled — add the first one.</p>
        )}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function communityKey(c: Community) {
  return `${c.supportedOrganisationId}:${c.scope}`;
}

function parseCommunityKey(key: string): { supportedOrganisationId: number; scope: string } | null {
  const idx = key.indexOf(':');
  if (idx === -1) return null;
  return { supportedOrganisationId: Number(key.slice(0, idx)), scope: key.slice(idx + 1) };
}

function communityLabel(c: Community) {
  return c.scope === '*' || !c.scopeLabel ? c.name : `${c.name} (${c.scopeLabel})`;
}

// ── Leadership Updates / Prayer Requests ──────────────────────────────────────

const POST_TYPE = {
  update: 'LEADERSHIP_UPDATE',
  prayer: 'LEADERSHIP_PRAYER_REQUEST',
} as const;

type DraftKind = keyof typeof POST_TYPE;
type Draft = { id: number; kind: DraftKind; title: string; description: string; imageUrls?: string[] };

function UpdatesComposer() {
  const [kind, setKind] = useState<DraftKind>('update');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [posted, setPosted] = useState<Draft[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [createPost, { loading }] = useMutation(CREATE_LEADERSHIP_POST);
  const [createImageUpload] = useMutation(CREATE_IMAGE_UPLOAD);

  const isPrayer = kind === 'prayer';
  const busy = loading || uploading;
  // Backend requires both title and description (non-null args).
  const canPost = title.trim() !== '' && description.trim() !== '' && !busy;

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const picked = Array.from(incoming).filter(f => f.type.startsWith('image/'));
    setFiles(prev => [...prev, ...picked]);
  };

  const removeFile = (index: number) =>
    setFiles(prev => prev.filter((_, i) => i !== index));

  // Upload each staged file to S3 via a presigned PUT and return the new image ids.
  const uploadStagedFiles = async (): Promise<number[]> => {
    const ids: number[] = [];
    for (const file of files) {
      const { data } = await createImageUpload({ variables: { fileName: file.name } });
      const upload = data?.createImageUpload;
      if (!upload) continue;
      await fetch(upload.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'application/octet-stream' },
      });
      ids.push(upload.image.id);
    }
    return ids;
  };

  const post = async () => {
    if (!canPost) return;
    setError(null);
    try {
      setUploading(true);
      const imageIds = files.length > 0 ? await uploadStagedFiles() : [];
      setUploading(false);

      const { data } = await createPost({
        variables: {
          title: title.trim(),
          description: description.trim(),
          type: POST_TYPE[kind],
          imageIds,
        },
      });
      const created = data?.createLeadershipPost;
      setPosted(p => [
        {
          id: created?.id ?? Date.now(),
          kind,
          title: title.trim(),
          description: description.trim(),
          imageUrls: (created?.images ?? []).map((img: any) => img.url),
        },
        ...p,
      ]);
      setTitle('');
      setDescription('');
      setFiles([]);
    } catch (e: any) {
      setError(e?.message ?? 'Something went wrong — please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Updates &amp; Prayer Requests</h2>
        <span className={s.sectionTag}>Org-wide</span>
      </div>

      {error && (
        <div className={s.stubBanner}>
          <span className={s.stubBannerIcon}>⚠</span>
          <span>{error}</span>
        </div>
      )}

      <div className={s.composer}>
        <div className={s.segment}>
          <button
            className={`${s.segmentBtn} ${!isPrayer ? s.segmentBtnActive : ''}`}
            onClick={() => setKind('update')}
          >
            Leadership Update
          </button>
          <button
            className={`${s.segmentBtn} ${isPrayer ? s.segmentBtnActive : ''}`}
            onClick={() => setKind('prayer')}
          >
            Prayer Request
          </button>
        </div>

        <div className={s.field}>
          <label className={s.fieldLabel}>Title</label>
          <input
            className={s.input}
            value={title}
            placeholder={isPrayer ? 'e.g. Pray for the upcoming outreach' : 'e.g. Term 2 highlights'}
            onChange={e => setTitle(e.target.value)}
          />
        </div>

        <div className={s.field}>
          <label className={s.fieldLabel}>{isPrayer ? 'Prayer request' : 'Update'}</label>
          <textarea
            className={s.textarea}
            value={description}
            placeholder={isPrayer ? 'Share what the community can pray for…' : 'Share an update with the community…'}
            onChange={e => setDescription(e.target.value)}
          />
        </div>

        <div className={s.field}>
          <label className={s.fieldLabel}>Photos</label>
          <div className={s.photoRow}>
            {files.map((file, i) => (
              <div key={`${file.name}-${i}`} className={s.photoThumb}>
                <img src={URL.createObjectURL(file)} alt={file.name} />
                <button
                  type="button"
                  className={s.photoRemove}
                  onClick={() => removeFile(i)}
                  aria-label="Remove photo"
                >
                  ×
                </button>
              </div>
            ))}
            <label className={s.photoAdd}>
              <span className={s.photoAddIcon}>+</span>
              <span>Add photo</span>
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
              />
            </label>
          </div>
        </div>

        <div className={s.composerFooter}>
          <button className={`${s.btn} ${s.btnPrimary}`} onClick={post} disabled={!canPost}>
            {uploading ? 'Uploading…' : loading ? 'Posting…' : isPrayer ? 'Post prayer request' : 'Post update'}
          </button>
        </div>
      </div>

      {posted.map(d => (
        <div key={d.id} className={`${s.draftCard} ${d.kind === 'prayer' ? s.draftCardPrayer : ''}`}>
          <span className={`${s.draftKind} ${d.kind === 'prayer' ? s.draftKindPrayer : ''}`}>
            {d.kind === 'prayer' ? 'Prayer Request' : 'Leadership Update'}
          </span>
          {d.title && <div className={s.draftTitle}>{d.title}</div>}
          {d.description && <div className={s.draftDescription}>{d.description}</div>}
          {d.imageUrls && d.imageUrls.length > 0 && (
            <div className={s.photoRow}>
              {d.imageUrls.map(url => (
                <div key={url} className={s.photoThumb}>
                  <img src={url} alt="" />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {posted.length === 0 && (
        <p className={s.empty}>Nothing posted this session — posts you publish will appear here.</p>
      )}
    </div>
  );
}

// ── Community picker prompt (shown in community-specific tabs) ────────────────

function CommunityPrompt({
  what,
  communities,
  onSelect,
}: {
  what: string;
  communities: Community[];
  onSelect: (key: string) => void;
}) {
  return (
    <div className={s.promptWrap}>
      <div className={s.promptIcon}>↑</div>
      <div className={s.promptTitle}>Select a community to manage {what}</div>
      <div className={s.promptHint}>
        {communities.length > 0
          ? 'Pick one below — it sets the community in the top bar too.'
          : 'No communities are available yet.'}
      </div>
      {communities.length > 0 && (
        <div className={s.promptOptions}>
          {communities.map(c => (
            <button
              key={communityKey(c)}
              className={s.promptOption}
              onClick={() => onSelect(communityKey(c))}
            >
              {communityLabel(c)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Root ─────────────────────────────────────────────────────────────────────

export function Leadership() {
  const { data: meData, loading: meLoading } = useQuery(GET_ME);
  const isPartnerLeadership = meData?.me?.organisation?.membership?.isPartnerLeadership ?? false;

  const { data: communitiesData } = useQuery(GET_SUPPORTED_COMMUNITIES);
  const communities: Community[] = communitiesData?.supportedCommunities ?? [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState<'joyride' | 'prayer' | 'visits' | 'updates'>('updates');

  // Auto-select once the data loads if the leader supports exactly one community.
  useEffect(() => {
    if (selectedKey === null && communities.length === 1) {
      setSelectedKey(communityKey(communities[0]));
    }
  }, [communities, selectedKey]);

  const selected = selectedKey ? parseCommunityKey(selectedKey) : null;

  // Non-leaders don't belong here — send them back to classes.
  // Wait for GET_ME so a real leader isn't bounced during the initial load.
  if (!meLoading && !isPartnerLeadership) {
    return <Navigate to={PATHS.CLASSES} replace />;
  }

  const communitySelector = (
    <select
      className={s.communitySelect}
      value={selectedKey ?? ''}
      onChange={e => setSelectedKey(e.target.value || null)}
    >
      <option value="">Select a community…</option>
      {communities.map(c => (
        <option key={communityKey(c)} value={communityKey(c)}>
          {communityLabel(c)}
        </option>
      ))}
    </select>
  );

  return (
    <div className={s.shell}>
      {/* Desktop top bar */}
      <header className={s.topBar}>
        <span className={s.topBarMark}>♦ Joyya Leadership</span>
        <span className={s.topBarSep} />
        <div className={s.topBarCommunityWrap}>
          {communities.length > 0
            ? communitySelector
            : <span className={s.topBarCommunityPlaceholder}>Loading communities…</span>}
        </div>
        <Link to={PATHS.LOGOUT} className={s.topBarLogout}>Log out</Link>
      </header>

      <div className={s.body}>
        {/* Mobile community selector */}
        <div className={`${s.mobileCommunityBar}`} style={{ display: 'none' }} id="mobile-community-bar">
          <div className={s.mobileCommunityLabel}>Community</div>
          {communitySelector}
        </div>

        {/* Tabs */}
        <div className={s.tabBar}>
          <button
            className={`${s.tab} ${tab === 'updates' ? s.tabActive : ''}`}
            onClick={() => setTab('updates')}
          >
            Updates &amp; Prayers
          </button>
          <button
            className={`${s.tab} ${tab === 'prayer' ? s.tabActive : ''}`}
            onClick={() => setTab('prayer')}
          >
            Prayer Meetings
          </button>
          <button
            className={`${s.tab} ${tab === 'visits' ? s.tabActive : ''}`}
            onClick={() => setTab('visits')}
          >
            Community Visits
          </button>
          <button
            className={`${s.tab} ${tab === 'joyride' ? s.tabActive : ''}`}
            onClick={() => setTab('joyride')}
          >
            Joyride
          </button>
        </div>

        {/* Joyride is org-wide — no community needed */}
        {tab === 'joyride' && <JoyrideSection />}

        {tab === 'prayer' && (
          selected != null
            ? <PrayerMeetingsSection orgId={selected.supportedOrganisationId} />
            : <CommunityPrompt what="prayer meetings" communities={communities} onSelect={setSelectedKey} />
        )}

        {tab === 'visits' && (
          selected != null
            ? <VisitsSection orgId={selected.supportedOrganisationId} />
            : <CommunityPrompt what="community visits" communities={communities} onSelect={setSelectedKey} />
        )}

        {tab === 'updates' && <UpdatesComposer />}
      </div>

      {/* Mobile nav — only shows on small screens via CSS override below */}
      <style>{`
        @media (max-width: 768px) {
          #mobile-community-bar { display: block !important; }
        }
      `}</style>
    </div>
  );
}
